import { useMemo, useState } from 'react';
import { useSharedCaption } from '@utils/providers/shared-caption-context.tsx';
import { useThemeContext } from '@utils/providers/theme-context.tsx';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import Modal from '@components/atoms/modals/modal.tsx';
import Button from '@components/atoms/ui-kit/button.tsx';

interface SharedTimingShiftControlProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function SharedTimingShiftControl({
  isOpen,
  onClose
}: SharedTimingShiftControlProps) {
  const { t } = useTranslation();
  const { document, getActiveIndex, applyTimingOffset } = useSharedCaption();
  const { getColour } = useThemeContext();

  const [sec, setSec] = useState<string>('2');

  const activeIdx = getActiveIndex();
  const hasActive = activeIdx !== null && activeIdx >= 0;
  const affectingAll = !hasActive;

  const deltaMs = useMemo(() => {
    const s = Number(sec || 0);
    const clamp = (v: number) => (Number.isFinite(v) ? v : 0);
    return clamp(Math.round(s * 1000));
  }, [sec]);

  const disabled = !document?.document?.length || deltaMs === 0;

  const scopeLabel = affectingAll
    ? t('all-segments')
    : `${t('from-segment')} #${activeIdx ?? 0}`;

  const presets = [0.5, 1, 1.5, 2];

  const apply = (sign: 1 | -1) => {
    if (disabled) return;
    applyTimingOffset(sign * deltaMs, 'auto');
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('timing-offset')}
      caption={t('timing-offset-info')}
      size="small"
      customWidth="420px"
      footerActions={[
        <Button
          key="done"
          onClick={onClose}
          label={t('done')}
          size="small"
          variant="primary"
        />
      ]}
    >
      <div className="flex flex-col gap-4">
        {/* Scope indicator */}
        <div
          className="flex items-center gap-2 p-3 rounded-2xl border"
          style={{
            backgroundColor: getColour('secondLayer'),
            borderColor: getColour('border')
          }}
        >
          <label
            className="text-[10px] uppercase tracking-wider font-medium"
            style={{ color: getColour('inactiveText') }}
          >
            {t('affecting')}
          </label>
          <span
            className="text-xs px-2 py-0.5 rounded-full font-medium"
            style={{
              backgroundColor: affectingAll
                ? getColour('thirdLayer')
                : getColour('activeText') + '20',
              color: affectingAll
                ? getColour('inactiveText')
                : getColour('activeText')
            }}
          >
            {scopeLabel}
          </span>
        </div>

        {/* Quick presets */}
        <div
          className="p-3 rounded-2xl border"
          style={{
            backgroundColor: getColour('secondLayer'),
            borderColor: getColour('border')
          }}
        >
          <label
            className="block text-[10px] font-medium mb-0.5 uppercase tracking-wider"
            style={{ color: getColour('inactiveText') }}
          >
            {t('one-click-shift')}
          </label>
          <p
            className="text-[11px] mb-3 leading-tight"
            style={{ color: getColour('inactiveText') }}
          >
            {t('quickly-shift-timing-by-preset-amounts')}
          </p>
          <div className="grid grid-cols-4 gap-1.5">
            {presets.map(p => (
              <div key={p} className="flex items-center">
                <button
                  type="button"
                  onClick={() => applyTimingOffset(-p * 1000, 'auto')}
                  className="h-8 w-full rounded-l text-xs font-medium font-mono tabular-nums border border-r-0 transition-all hover:opacity-80 active:scale-95"
                  style={{
                    borderColor: getColour('activeText') + '40',
                    backgroundColor: getColour('activeText') + '10',
                    color: getColour('activeText')
                  }}
                >
                  −{p}s
                </button>
                <button
                  type="button"
                  onClick={() => applyTimingOffset(p * 1000, 'auto')}
                  className="h-8 w-full rounded-r text-xs font-medium font-mono tabular-nums border transition-all hover:opacity-80 active:scale-95"
                  style={{
                    borderColor: getColour('activeText') + '40',
                    backgroundColor: getColour('activeText') + '10',
                    color: getColour('activeText')
                  }}
                >
                  +{p}s
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Custom offset */}
        <div
          className="p-3 rounded-2xl border"
          style={{
            backgroundColor: getColour('secondLayer'),
            borderColor: getColour('border')
          }}
        >
          <label
            className="block text-[10px] font-medium mb-0.5 uppercase tracking-wider"
            style={{ color: getColour('inactiveText') }}
          >
            {t('custom-offset')}
          </label>
          <p
            className="text-[11px] mb-3 leading-tight"
            style={{ color: getColour('inactiveText') }}
          >
            {t('enter-a-custom-time-offset-in-seconds')}
          </p>
          <div className="flex items-center justify-start">
            <button
              disabled={disabled}
              onClick={() => apply(-1)}
              className="h-9 w-10 rounded-l text-sm font-medium border border-r-0 transition-all hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95"
              style={{
                borderColor: getColour('border'),
                backgroundColor: getColour('thirdLayer'),
                color: getColour('normalText')
              }}
            >
              −
            </button>
            <div className="relative">
              <input
                type="number"
                inputMode="decimal"
                className={cn(
                  'h-9 w-20 border-y px-2 pr-5 text-sm text-center font-mono tabular-nums',
                  'focus:outline-none focus:ring-1'
                )}
                style={{
                  borderColor: getColour('border'),
                  backgroundColor: getColour('secondLayer'),
                  color: getColour('normalText')
                }}
                value={sec}
                onChange={e => setSec(e.target.value)}
                min={0}
                step={0.1}
              />
              <span
                className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-medium"
                style={{ color: getColour('inactiveText') }}
              >
                s
              </span>
            </div>
            <button
              disabled={disabled}
              onClick={() => apply(+1)}
              className="h-9 w-10 rounded-r text-sm font-medium border border-l-0 transition-all hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95"
              style={{
                borderColor: getColour('border'),
                backgroundColor: getColour('thirdLayer'),
                color: getColour('normalText')
              }}
            >
              +
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
